// 개별일정 등록 #/register — 학생 검색 → 날짜 → 시간 → 등록(세 번 탭). v0.3 Register.kt · SCREENS §1-3
// compact: 3단계 탭 + 하단 고정 등록 바 · medium: 2단 · expanded: 3단 · large: 4단(이 학생 이번 주 일정 미리보기)
import { useEffect, useMemo, useState } from "react";
import { deleteSession, registerSession } from "../data/actions";
import { nextSlot, openItems, sessionOrder, studentMap, upcoming } from "../data/select";
import { useView } from "../data/store";
import type { Student } from "../data/types";
import { addDays, dowLabel, dowValue, fmtDate, parseYmd, relDate, todayStr, weekOfMonth } from "../lib/date";
import { go, usePref, useWidthClass, type Route } from "../ui/hooks";
import { Icon } from "../ui/icons";
import { Empty, StatusPill, StudentName, toast } from "../ui/parts";
import { Loading } from "./Dashboard";

type NewItem = { week: string; title: string };

export function Register({ route }: { route: Route }) {
  const v = useView();
  const wc = useWidthClass();
  const sm = studentMap(v);
  const pre = route.query.get("student");
  const [sid, setSid] = useState<string | null>(null);
  const [usedPre, setUsedPre] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [items, setItems] = useState<NewItem[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [week, setWeek] = useState(`${weekOfMonth()}주차`);
  const [custom, setCustom] = useState("");
  const [cont, setCont] = usePref<"on" | "off">("centium.regContinuous", "on");
  const [count, setCount] = useState(0);
  const [last, setLast] = useState<string | null>(null);
  const st = sid ? sm.get(sid) : undefined;

  const choose = (id: string) => {
    setSid(id);
    setQ("");
    setItems([]);
    setAddOpen(false);
    const n = nextSlot(sm.get(id));
    if (n) { setDate(n.date); setTime(n.time); }
    setStep(2);
  };

  // 대시보드 «일정 잡기»처럼 학생이 미리 정해져 들어오면 기본 날짜·시간을 채운다(데이터가 늦게 와도)
  useEffect(() => {
    if (pre && pre !== usedPre && sm.has(pre)) { setUsedPre(pre); choose(pre); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pre, v.rev]);

  if (!v.status.loaded) return <Loading />;

  const dup = st && date ? v.sessions.filter((s) => s.studentId === st.id && s.date === date).sort(sessionOrder) : [];
  const ok = !!st && !!date && !!time;

  const submit = () => {
    if (!st || !date || !time) return;
    const s = registerSession(st.id, date, time, items);
    const label = `${st.name} · ${fmtDate(date)} ${time}`;
    setCount((c) => c + 1);
    setLast(label + " 등록" + (items.length ? ` (+시험지 ${items.length})` : ""));
    toast(`${label} 등록했습니다`, { undo: () => { deleteSession(s.id); setCount((c) => Math.max(0, c - 1)); } });
    setSid(null);
    setItems([]);
    setAddOpen(false);
    setStep(1);
    if (pre) go("register");
    if (cont === "off") { setDate(null); setTime(null); go(""); }
  };

  const studentPanel = (
    <section className="panel" aria-labelledby="reg-1">
      <h2 className="panel-h" id="reg-1"><span className="step-n">1</span> 학생</h2>
      {!st ? (
        <StudentPicker students={v.students} q={q} setQ={setQ} onPick={choose} openCount={(id) => openItems(v, id).length} />
      ) : (
        <>
          <div className="picked">
            <div className="picked-top">
              <StudentName st={st} />
              <span className="grow" />
              <button type="button" className="btn btn-ghost" onClick={() => { setSid(null); setItems([]); setStep(1); if (pre) go("register"); }}>다른 학생</button>
            </div>
            <OpenSummary st={st} open={openItems(v, st.id).map((i) => i.title)} />
            {upcoming(v, st.id).length ? (
              <p className="warn-text">이미 잡힌 일정: {upcoming(v, st.id).map((s) => `${relDate(s.date)} ${s.time}`).join(", ")}</p>
            ) : null}
          </div>
          {!addOpen && !items.length ? (
            <button type="button" className="btn btn-ghost btn-block" onClick={() => setAddOpen(true)}><Icon name="plus" size={18} /> 오늘 남은 시험지 추가 (선택)</button>
          ) : (
            <div className="add-items">
              <p className="label">오늘 남은 시험지 (선택)</p>
              <div className="chips">
                {[1, 2, 3, 4, 5].map((n) => `${n}주차`).map((w) => (
                  <button key={w} type="button" className={"chip" + (week === w ? " on" : "")} aria-pressed={week === w} onClick={() => setWeek(w)}>{w}</button>
                ))}
              </div>
              <div className="chips">
                {v.settings.presets.map((p) => {
                  const on = items.some((i) => i.week === week && i.title === p);
                  return (
                    <button key={p} type="button" className={"chip" + (on ? " on" : "")} aria-pressed={on}
                      onClick={() => setItems(on ? items.filter((i) => !(i.week === week && i.title === p)) : [...items, { week, title: p }])}>{p}</button>
                  );
                })}
              </div>
              <form className="inline-form" onSubmit={(e) => { e.preventDefault(); if (custom.trim()) { setItems([...items, { week, title: custom.trim() }]); setCustom(""); } }}>
                <input className="input" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="직접 입력 (예: 부교재 16강 1~4번)" aria-label="시험지 직접 입력" />
                <button type="submit" className="btn">추가</button>
              </form>
              {items.length ? <p className="muted small">추가될 항목: {items.map((i) => `[${i.week}] ${i.title}`).join(", ")}</p> : null}
            </div>
          )}
        </>
      )}
    </section>
  );

  const datePanel = (
    <section className="panel" aria-labelledby="reg-2">
      <h2 className="panel-h" id="reg-2"><span className="step-n">2</span> 날짜</h2>
      <DatePicker value={date} onPick={(d) => { setDate(d); setStep(3); }} />
    </section>
  );

  const timePanel = (
    <section className="panel" aria-labelledby="reg-3">
      <h2 className="panel-h" id="reg-3"><span className="step-n">3</span> 시간</h2>
      <TimePicker times={v.settings.times} value={time} onPick={setTime} />
    </section>
  );

  const summary = (
    <div className="reg-summary">
      {dup.length ? (
        <div className="banner banner-warn" role="alert">
          <span className="grow">같은 날 이미 일정이 있습니다: {dup.map((s) => `${s.time} ${s.status === "예정" ? "대기" : s.status}`).join(", ")} — 그래도 등록할 수 있습니다.</span>
        </div>
      ) : null}
      {last ? <p className="done-text"><Icon name="check" size={18} /> {last}{count > 1 ? ` · 이번에 ${count}건` : ""}</p> : null}
      <div className="reg-actions">
        <button type="button" role="switch" aria-checked={cont === "on"} className={"switch" + (cont === "on" ? " on" : "")} onClick={() => setCont(cont === "on" ? "off" : "on")}>
          <span className="switch-track"><span className="switch-thumb" /></span>
          <span>연속 등록</span>
        </button>
        <button type="button" className="btn btn-primary btn-lg grow" disabled={!ok} onClick={submit}>
          {ok ? `${st!.name} · ${fmtDate(date!)} ${time} 등록` : !st ? "학생을 고르세요" : "날짜와 시간을 고르세요"}
        </button>
      </div>
    </div>
  );

  if (wc === "compact") {
    return (
      <div className="page reg reg-compact">
        <div className="steps" role="tablist" aria-label="등록 단계">
          {([1, 2, 3] as const).map((n) => (
            <button key={n} type="button" role="tab" aria-selected={step === n} className={"step" + (step === n ? " on" : "")} onClick={() => setStep(n)}>
              <span className="step-n">{n}</span>
              <span className="step-t">{n === 1 ? (st ? st.name : "학생") : n === 2 ? (date ? fmtDate(date) : "날짜") : (time ?? "시간")}</span>
            </button>
          ))}
        </div>
        {step === 1 ? studentPanel : step === 2 ? datePanel : timePanel}
        {count ? <p className="muted small center">이번에 {count}건 등록 · 학생만 비우고 날짜·시간은 그대로 둡니다</p> : null}
        <div className="actionbar">{summary}</div>
      </div>
    );
  }
  return (
    <div className={"page reg reg-" + wc}>
      <div className="reg-grid">
        {studentPanel}
        {wc === "medium" ? <div className="reg-stack">{datePanel}{timePanel}</div> : <>{datePanel}{timePanel}</>}
        {wc === "large" ? <WeekPreview st={st} date={date} /> : null}
      </div>
      {summary}
      <p className="muted small">등록하면 학생만 비워지고 다음 학생을 이어서 등록할 수 있습니다{count ? ` · 이번에 ${count}건` : ""}.</p>
    </div>
  );
}

function StudentPicker({ students, q, setQ, onPick, openCount }: { students: Student[]; q: string; setQ: (s: string) => void; onPick: (id: string) => void; openCount: (id: string) => number }) {
  const t = q.trim();
  const list = useMemo(() => students.filter((s) => !t || s.name.includes(t) || s.school.includes(t)), [students, t]);
  return (
    <>
      <label className="search">
        <Icon name="search" size={20} />
        <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="학생 이름 또는 학교 검색" aria-label="학생 검색" autoComplete="off" />
      </label>
      {list.length === 0 ? <Empty>검색 결과가 없습니다.</Empty> : (
        <ul className="pick-list">
          {list.slice(0, 40).map((s) => {
            const n = openCount(s.id);
            return (
              <li key={s.id}>
                <button type="button" className="pick" onClick={() => onPick(s.id)}>
                  <StudentName st={s} />
                  <span className="grow" />
                  {n ? <span className="count">{n}</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

function OpenSummary({ open }: { st: Student; open: string[] }) {
  return <p className="muted small">{open.length === 0 ? "남은 클리닉 없음" : `남은 클리닉 ${open.length}개 · ${open.slice(0, 3).join(", ")}${open.length > 3 ? " 외" : ""}`}</p>;
}

function DatePicker({ value, onPick }: { value: string | null; onPick: (d: string) => void }) {
  const td = todayStr();
  const days = Array.from({ length: 8 }, (_, i) => addDays(td, i));
  const other = value !== null && !days.includes(value);
  return (
    <>
      <div className="chips chips-date">
        {days.map((d, i) => (
          <button key={d} type="button" className={"chip chip-date" + (value === d ? " on" : "")} aria-pressed={value === d} onClick={() => onPick(d)}>
            <b>{i === 0 ? "오늘" : i === 1 ? "내일" : dowLabel(d)}</b>
            <span>{parseYmd(d).getMonth() + 1}/{parseYmd(d).getDate()}</span>
          </button>
        ))}
      </div>
      <label className="field">
        <span className="label">다른 날{other ? ` · ${fmtDate(value!)}` : ""}</span>
        <input className="input" type="date" value={value ?? ""} min={addDays(td, -30)} onChange={(e) => e.target.value && onPick(e.target.value)} />
      </label>
    </>
  );
}

function TimePicker({ times, value, onPick }: { times: string[]; value: string | null; onPick: (t: string) => void }) {
  const other = value !== null && !times.includes(value);
  return (
    <>
      <div className="time-grid">
        {times.map((t) => (
          <button key={t} type="button" className={"chip chip-time" + (value === t ? " on" : "")} aria-pressed={value === t} onClick={() => onPick(t)}>{t}</button>
        ))}
      </div>
      <label className="field">
        <span className="label">직접{other ? ` · ${value}` : ""}</span>
        <input className="input" type="time" step={600} value={value ?? ""} onChange={(e) => e.target.value && onPick(e.target.value)} />
      </label>
    </>
  );
}

function WeekPreview({ st, date }: { st?: Student; date: string | null }) {
  const v = useView();
  const anchor = date ?? todayStr();
  const mon = addDays(anchor, 1 - dowValue(anchor));
  const sun = addDays(mon, 6);
  const list = st ? v.sessions.filter((s) => s.studentId === st.id && s.date >= mon && s.date <= sun).sort(sessionOrder) : [];
  return (
    <section className="panel" aria-labelledby="reg-4">
      <h2 className="panel-h" id="reg-4">이 학생 이번 주 일정</h2>
      <p className="muted small">{fmtDate(mon)} ~ {fmtDate(sun)}</p>
      {!st ? <Empty>학생을 고르면 그 주 일정이 보입니다.</Empty> : list.length === 0 ? <Empty>이 주에 잡힌 일정이 없습니다.</Empty> : (
        <ul className="list">
          {list.map((s) => (
            <li key={s.id} className="row-card static">
              <span className="time">{fmtDate(s.date)} {s.time}</span>
              <span className="grow" />
              <StatusPill status={s.status} />
            </li>
          ))}
        </ul>
      )}
      {st && st.slots?.length ? <p className="muted small">정규: {st.slots.map((s) => `${["월", "화", "수", "목", "금", "토", "일"][s.dow - 1]} ${s.time}`).join(" · ")}</p> : null}
    </section>
  );
}
